<div class="card">
  <div class="card-content">

    <!-- Stats: shared .stats-cards-row / .stats-card so this page reads like the others.
         Deals and Activities are split rather than shown as one total — the whole point of
         the trial is seeing the two populations side by side. -->
    <div class="stats-cards-row">
      <div class="stats-card stats-card--total">
        <div class="stats-card-main">
          <span class="stats-card-value">{{ totalCardCount }}</span>
          <span class="stats-card-label">On the board</span>
          <span class="stats-card-sublabel">
            {{ projectCardCount }} {{ projectCardCount === 1 ? 'deal' : 'deals' }}
            · {{ activityCardCount }} {{ activityCardCount === 1 ? 'activity' : 'activities' }}
          </span>
        </div>
        <div class="stats-card-icon"><i class="pi pi-objects-column"></i></div>
      </div>

      <!-- Overdue and Unassigned are the two counts worth acting on. -->
      <div class="stats-card stats-card--total">
        <div class="stats-card-main">
          <span class="stats-card-value">{{ overdueCardCount }}</span>
          <span class="stats-card-label">Overdue</span>
          <span class="stats-card-sublabel">Past their date</span>
        </div>
        <div class="stats-card-icon"><i class="pi pi-exclamation-triangle"></i></div>
      </div>

      <div class="stats-card stats-card--total">
        <div class="stats-card-main">
          <span class="stats-card-value">{{ unassignedCardCount }}</span>
          <span class="stats-card-label">Unassigned</span>
          <span class="stats-card-sublabel">No owner set</span>
        </div>
        <div class="stats-card-icon"><i class="pi pi-user"></i></div>
      </div>
    </div>

    <!-- Filters: shared .filter-bar, shown/hidden by the main header filter toggle -->
    <div class="filter-bar filter-bar-collapsible mb-4"
         [class.filter-bar-visible]="pageHeaderService.currentFiltersVisible">
      <div class="filter-bar-filters">
        <!-- The consolidation control: both on by default, either collapsible back to a
             single-source view. -->
        <div class="filter-field pcb-filter-show">
          <label class="filter-label">Show</label>
          <div class="pcb-show-toggles">
            <label class="pcb-show-toggle">
              <p-checkbox inputId="pcb-show-projects" [(ngModel)]="showProjects" [binary]="true" />
              <span>Deals</span>
            </label>
            <label class="pcb-show-toggle">
              <p-checkbox inputId="pcb-show-activities" [(ngModel)]="showActivities" [binary]="true" />
              <span>Activities</span>
            </label>
          </div>
        </div>

        <div class="filter-field">
          <label for="pcb-owner" class="filter-label">Owner / PM</label>
          <p-multiSelect id="pcb-owner" [options]="ownerOptions" [(ngModel)]="selectedOwners"
                         optionLabel="label" optionValue="value" placeholder="All owners"
                         [maxSelectedLabels]="1" selectedItemsLabel="{0} selected"
                         [showToggleAll]="true" [filter]="true" filterPlaceholder="Search..."
                         panelStyleClass="filter-dropdown-panel" appendTo="body" />
        </div>

        <div class="filter-field">
          <label for="pcb-status" class="filter-label">Status</label>
          <p-multiSelect id="pcb-status" [options]="statusOptions" [(ngModel)]="selectedStatuses"
                         optionLabel="label" optionValue="value" placeholder="All statuses"
                         [maxSelectedLabels]="1" selectedItemsLabel="{0} selected"
                         [showToggleAll]="true"
                         panelStyleClass="filter-dropdown-panel" appendTo="body" />
        </div>

        <div class="filter-field pcb-filter-compact">
          <label for="pcb-hide-empty" class="filter-label">Hide empty</label>
          <div class="pcb-check-cell">
            <input id="pcb-hide-empty" type="checkbox" [(ngModel)]="hideEmptyColumns"
                   pTooltip="Hide columns with no cards" tooltipPosition="top" />
          </div>
        </div>

        <div class="filter-field pcb-filter-compact" *ngIf="hasActiveFilters">
          <label class="filter-label">&nbsp;</label>
          <button type="button" class="btn-clear-filters" (click)="clearFilters()"
                  pTooltip="Clear all filters" tooltipPosition="top">
            <i class="pi pi-filter-slash"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Loading -->
    <div *ngIf="loading" class="loading-container loading-container--centered">
      <i class="pi pi-spin pi-spinner loading-spinner-icon"></i>
      <p>Loading the board...</p>
    </div>

    <!-- Board. Reuses the kanban classes from _presales.scss so it reads like the deal
         board rather than inventing a second board language. -->
    <div *ngIf="!loading" class="kanban-view kanban-view--progress">
      <!-- Nothing ticked is a filter state, not an empty dataset, so it says so plainly. -->
      <div class="empty-state" *ngIf="noCardTypeSelected">
        <div class="empty-icon"><i class="pi pi-filter-slash"></i></div>
        <p class="empty-message">Nothing selected to show</p>
        <p class="empty-description">Tick "Deals" or "Activities" in the filters to populate the board.</p>
      </div>

      <div class="empty-state" *ngIf="!noCardTypeSelected && columns.length === 0">
        <div class="empty-icon"><i class="pi pi-inbox"></i></div>
        <p class="empty-message">Nothing on the board</p>
        <p class="empty-description">
          {{ hasActiveFilters
              ? 'Clear a filter, or untick "Hide empty" to see every column'
              : 'Create a presales deal or an activity to get started' }}
        </p>
      </div>

      <div class="kanban-scroll" *ngIf="!noCardTypeSelected && columns.length > 0">
        <div class="kanban-grid">
          <div class="kanban-col" *ngFor="let col of columns"
               [ngClass]="col.cssClass"
               [class.is-empty]="col.cards.length === 0">
            <div class="kanban-col-head">
              <div class="kanban-col-head-top">
                <span class="kanban-col-title">
                  <span class="dot" [ngClass]="col.cssClass"></span>{{ col.label }}
                </span>
                <span class="kanban-col-count">{{ col.cards.length }}</span>
              </div>
              <span class="kanban-col-sub pcb-col-sub--overdue" *ngIf="columnOverdueCount(col) > 0">
                {{ columnOverdueCount(col) }} overdue
              </span>
            </div>

            <div class="kanban-col-body">
              <!-- Same card classes as the other presales boards, plus a type stripe and
                   badge: on a mixed board the first thing to establish is which of the two
                   kinds of record you are looking at. -->
              <div class="kanban-card kanban-card--rich pcb-card"
                   *ngFor="let card of col.cards"
                   [class.pcb-card--project]="card.kind === 'project'"
                   [class.pcb-card--activity]="card.kind === 'task'"
                   [class.is-overdue]="overdueDays(card) !== null"
                   [class.is-clickable]="canManage"
                   (click)="onCardClick(card)"
                   [pTooltip]="canManage
                      ? (card.kind === 'project'
                          ? 'Open this deal in Presales Inventory'
                          : 'Open this activity in the Activity Tracker')
                      : ''"
                   tooltipPosition="left"
                   [tooltipOptions]="{ showDelay: 700 }">

                <!-- Row 1: what kind of record this is, then its RAG dot. -->
                <div class="kanban-card-top">
                  <span class="pcb-kind">
                    <i class="pi" aria-hidden="true"
                       [ngClass]="card.kind === 'project' ? 'pi-briefcase' : 'pi-check-square'"></i>
                    {{ card.kind === 'project' ? 'Deal' : 'Activity' }}
                  </span>
                  <span class="k-rag" *ngIf="card.status"
                        [ngClass]="statusClass(card.status)"
                        [pTooltip]="'Health: ' + card.status" tooltipPosition="top"></span>
                </div>

                <!-- Row 2: the name as the headline. -->
                <h4 class="kanban-card-title" [pTooltip]="card.name" tooltipPosition="top"
                    [tooltipOptions]="{ showDelay: 600 }">{{ card.name }}</h4>

                <!-- Row 3: context. A deal carries its client here; an activity carries the
                     deal it belongs to, then that deal's client beneath. -->
                <div class="kanban-card-meta">
                  <span class="k-client"
                        [pTooltip]="card.kind === 'project'
                          ? (card.subtitle || 'No client set')
                          : (card.subtitle || 'Standalone activity')"
                        tooltipPosition="top">
                    <i class="pi" aria-hidden="true"
                       [ngClass]="card.kind === 'project' ? 'pi-building' : 'pi-briefcase'"></i>
                    {{ card.kind === 'project'
                        ? (card.subtitle || 'No client set')
                        : (card.subtitle || 'Standalone') }}
                  </span>
                </div>
                <div class="kanban-card-meta" *ngIf="card.kind === 'task' && card.subtitle">
                  <span class="k-client" [pTooltip]="card.clientName || 'No client set'"
                        tooltipPosition="top">
                    <i class="pi pi-building" aria-hidden="true"></i>{{ card.clientName || 'No client set' }}
                  </span>
                </div>

                <!-- Row 4: progress bar, on activities only. A deal has no numeric
                     completion, and a 0% bar would read as "not started". -->
                <div class="kanban-card-value" *ngIf="card.progressPercentage !== null">
                  <span class="k-win" [class.k-win--high]="(card.progressPercentage || 0) >= 70"
                        pTooltip="Progress" tooltipPosition="top">
                    <span class="k-win-bar"><span class="k-win-fill"
                          [style.width.%]="card.progressPercentage || 0"></span></span>
                    {{ card.progressPercentage || 0 }}%
                  </span>
                </div>

                <!-- Description and notes: two-line clamp, full text on hover. -->
                <div class="kanban-card-comments" *ngIf="card.description"
                     [pTooltip]="card.description" tooltipPosition="top"
                     [tooltipOptions]="{ showDelay: 500 }">
                  {{ card.description }}
                </div>
                <div class="kanban-card-comments" *ngIf="card.comments"
                     [pTooltip]="card.comments" tooltipPosition="top"
                     [tooltipOptions]="{ showDelay: 500 }">
                  <i class="pi pi-comment pcb-note-icon" aria-hidden="true"></i>{{ card.comments }}
                </div>

                <!-- Foot: who owns it, and when it lands. Same shape as the other boards —
                     owner on the left, date or overdue stamp on the right. -->
                <div class="kanban-card-foot">
                  <span class="k-owner"
                        [pTooltip]="(card.kind === 'project' ? 'Presale PM: ' : 'Sales Owner: ')
                          + (card.ownerName || 'Unassigned')"
                        tooltipPosition="top">
                    <span class="k-avatar" [style.--k-hue]="avatarHue(card.ownerName)">
                      {{ initials(card.ownerName) }}
                    </span>
                    <span class="k-owner-name">{{ card.ownerName || 'Unassigned' }}</span>
                  </span>
                  <span class="k-close k-overdue" *ngIf="overdueDays(card) !== null">
                    <i class="pi pi-exclamation-circle"></i>{{ overdueDays(card) }}d over
                  </span>
                  <span class="k-close" *ngIf="overdueDays(card) === null && card.targetDate"
                        [pTooltip]="card.kind === 'project' ? 'Expected close date' : 'End date'"
                        tooltipPosition="top">
                    <i class="pi pi-calendar"></i>{{ formatDate(card.targetDate) }}
                  </span>
                </div>
              </div>

              <div class="kanban-empty" *ngIf="col.cards.length === 0">
                Nothing here
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
